import React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import ProjectContext from 'modules/project/providers/project/context';
import {
  createProjectNumberFormatter,
  createProjectNumberParser
} from 'utils';

type DecimalSeparator = 0 | 1 | 'Dot' | 'Comma';
type ThousandsSeparator = 0 | 1 | 2 | 'Comma' | 'Dot' | 'Space';

type InputOrTextArea = HTMLInputElement | HTMLTextAreaElement;

export type ProjectNumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | undefined | null;
  onChange?: (value: number | undefined, event: React.ChangeEvent<InputOrTextArea>) => void;
  decimalPlaces: number;
  decimalSeparator?: DecimalSeparator;
  thousandsSeparator?: ThousandsSeparator;
};

const ProjectNumberField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  decimalPlaces,
  decimalSeparator,
  thousandsSeparator,
  inputProps,
  ...rest
}: ProjectNumberFieldProps) => {
  const projectContext = React.useContext(ProjectContext);
  const effectiveDecimalSeparator = decimalSeparator ?? projectContext?.state.decimalSeparator;
  const effectiveThousandsSeparator = thousandsSeparator ?? projectContext?.state.thousandsSeparator;

  const formatNumber = createProjectNumberFormatter(effectiveDecimalSeparator, effectiveThousandsSeparator);
  const parseNumber = createProjectNumberParser(effectiveDecimalSeparator, effectiveThousandsSeparator);

  const [isFocused, setIsFocused] = React.useState(false);
  const [rawInput, setRawInput] = React.useState<string>('');

  const formattedFromProp = value !== undefined && value !== null && Number.isFinite(value)
    ? formatNumber(value, decimalPlaces)
    : '';

  const displayValue = isFocused ? rawInput : formattedFromProp;

  const handleFocus = (event: React.FocusEvent<InputOrTextArea>) => {
    setRawInput(formattedFromProp);
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<InputOrTextArea>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const handleChange = (event: React.ChangeEvent<InputOrTextArea>) => {
    const text = event.target.value;
    setRawInput(text);
    onChange?.(parseNumber(text), event);
  };

  return (
    <TextField
      {...rest}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{ inputMode: 'decimal', ...inputProps }}
    />
  );
};

export default ProjectNumberField;
